import { resolveAppLanguage } from '../../../i18n/language';

export function getDocLocale(language) {
  const lang = resolveAppLanguage(language);
  if (lang === 'zh') return 'zh-CN';
  if (lang === 'ar') return 'ar';
  return 'en';
}

export function trimValue(value) {
  return value != null && String(value).trim() !== '' ? String(value).trim() : '';
}

export function customerName(customer) {
  if (!customer) return '';
  const first = customer.first_name || '';
  const last = customer.last_name == null ? '' : customer.last_name;
  return `${first} ${last}`.trim();
}

export function normalizeAppliedRules(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.results)) return raw.results;
  return [];
}

/** Sum non-excluded applied rule amounts for a shipment. */
export function sumAppliedRuleAmounts(rules) {
  return normalizeAppliedRules(rules).reduce((acc, rule) => {
    if (!rule || rule.excluded) return acc;
    const amount = Number(rule.amount);
    return acc + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

/**
 * Billed total for one shipment.
 * When applied_rules are present, sum non-excluded rule amounts.
 * Otherwise fall back to shipment.price (legacy invoices without rules).
 */
export function shipmentBilledTotal(shipment) {
  if (!shipment) return 0;
  const rules = normalizeAppliedRules(shipment.applied_rules);
  if (rules.length > 0) return sumAppliedRuleAmounts(rules);
  const price = Number(shipment.price);
  return Number.isFinite(price) ? price : 0;
}

export function shipmentTotal(shipments) {
  if (!shipments || shipments.length === 0) return 0;
  return shipments.reduce((acc, s) => acc + shipmentBilledTotal(s), 0);
}

export function isDateOnlyString(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value ?? '').trim());
}

const DOC_TIME_OPTIONS = {
  hour: '2-digit',
  minute: '2-digit',
};

function formatDdMmYyyy(day, month, year) {
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
}

export function formatLocalDateOnly(dateString) {
  const [y, m, d] = String(dateString).trim().split('-').map(Number);
  return formatDdMmYyyy(d, m, y);
}

export function formatDocDateTime(value, dateLocale, emDash = '—') {
  if (value == null || String(value).trim() === '') return emDash;
  if (isDateOnlyString(value)) {
    return formatLocalDateOnly(value);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const datePart = formatDdMmYyyy(date.getDate(), date.getMonth() + 1, date.getFullYear());
  const timePart = date.toLocaleTimeString(dateLocale, DOC_TIME_OPTIONS);
  return `${datePart} ${timePart}`;
}

export const DOC_COLORS = {
  accent: '#000000',
  text: '#0f172a',
  muted: '#64748b',
  border: '#e2e8f0',
  boxBg: '#f8fafc',
};

export const DOC_FONT_STACK =
  '"Noto Sans", "Noto Sans Arabic", "Noto Sans SC", Inter, Arial, sans-serif';

export const DOC_PRINT_FONT_LINK =
  'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;700&family=Noto+Sans+SC:wght@400;700&family=Noto+Sans:wght@400;700&display=swap';

export const DOC_PRINT_CSS = `
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: ${DOC_FONT_STACK};
    font-size: 10pt;
    line-height: 1.45;
    color: ${DOC_COLORS.text};
  }
  .doc-page { width: 100%; }
  .doc-top-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }
  .doc-title {
    font-size: 11pt;
    letter-spacing: 1.2px;
    font-weight: 700;
    color: ${DOC_COLORS.accent};
  }
  .doc-id-badge { font-size: 10pt; font-weight: 700; }
  .doc-divider {
    height: 1px;
    background: ${DOC_COLORS.border};
    margin: 20px 0;
  }
  .doc-amount-card {
    background: ${DOC_COLORS.boxBg};
    border: 1px solid ${DOC_COLORS.border};
    border-radius: 6px;
    padding: 18px 20px;
    margin-bottom: 22px;
    text-align: center;
  }
  .doc-amount-label {
    font-size: 9pt;
    color: ${DOC_COLORS.muted};
    margin-bottom: 6px;
  }
  .doc-amount-value {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 26pt;
    font-weight: 700;
    color: ${DOC_COLORS.accent};
  }
  .doc-section-title {
    font-size: 10pt;
    font-weight: 700;
    margin: 0 0 10px;
  }
  .doc-detail-row {
    display: flex;
    padding: 7px 0;
    border-bottom: 1px solid ${DOC_COLORS.border};
  }
  .doc-detail-row:last-child { border-bottom: 0; }
  .doc-detail-label {
    width: 38%;
    font-size: 9pt;
    color: ${DOC_COLORS.muted};
  }
  .doc-detail-value {
    width: 62%;
    font-size: 10pt;
    font-weight: 700;
  }
  .doc-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 8px;
    font-size: 8pt;
  }
  .doc-table th,
  .doc-table td {
    border: 1px solid ${DOC_COLORS.border};
    padding: 5px;
    text-align: start;
  }
  .doc-table th {
    background: ${DOC_COLORS.boxBg};
    font-weight: 700;
    color: ${DOC_COLORS.accent};
  }
  .doc-table tfoot td {
    background: ${DOC_COLORS.boxBg};
    font-weight: 700;
  }
  .doc-amount-inline {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .doc-notes-box {
    margin-top: 22px;
    padding: 12px;
    background: ${DOC_COLORS.boxBg};
    border-radius: 6px;
    border: 1px solid ${DOC_COLORS.border};
  }
  .doc-notes-title {
    font-size: 9pt;
    font-weight: 700;
    color: ${DOC_COLORS.muted};
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .doc-notes-body { font-size: 9pt; line-height: 1.5; white-space: pre-wrap; }
  .doc-footer-thanks {
    margin-top: 28px;
    font-size: 8pt;
    color: ${DOC_COLORS.muted};
    text-align: center;
  }
  .doc-muted { color: ${DOC_COLORS.muted}; font-size: 9pt; }
  .doc-brand-row { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 4px; }
  .doc-brand-logo { width: 52px; height: 52px; object-fit: contain; }
  .doc-brand-name { font-size: 22pt; font-weight: 700; margin: 0 0 2px; }
  .doc-company-line { font-size: 8pt; color: ${DOC_COLORS.muted}; margin: 0 0 2px; line-height: 1.35; }
  .doc-footer-box {
    margin-top: 18px;
    padding-top: 12px;
    border-top: 1px solid ${DOC_COLORS.border};
  }
  .doc-footer-title {
    font-size: 8pt;
    font-weight: 700;
    color: ${DOC_COLORS.muted};
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .doc-footer-line { font-size: 8pt; line-height: 1.45; margin: 0 0 2px; white-space: pre-wrap; }
  .doc-refund-line {
    margin-top: 6px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 8pt;
    color: ${DOC_COLORS.muted};
  }
  .doc-refund-segment { display: inline-flex; align-items: center; gap: 3px; }
  .doc-summary-box {
    margin-top: 14px;
    margin-left: auto;
    max-width: 320px;
    border: 1px solid ${DOC_COLORS.border};
    border-radius: 6px;
    overflow: hidden;
  }
  .doc-summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-bottom: 1px solid ${DOC_COLORS.border};
    font-size: 9pt;
  }
  .doc-summary-row:last-child { border-bottom: 0; }
  .doc-summary-label { color: ${DOC_COLORS.muted}; }
  .doc-summary-value {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 700;
    text-align: end;
  }
  .doc-summary-row-total {
    background: ${DOC_COLORS.boxBg};
    font-size: 10pt;
  }
  .doc-summary-row-total .doc-summary-label,
  .doc-summary-row-total .doc-summary-value {
    color: ${DOC_COLORS.accent};
    font-weight: 700;
  }
  .doc-summary-row-overpaid .doc-summary-value { color: #b45309; }
  .doc-summary-row-paid .doc-summary-value { color: #047857; }
  .doc-tx-status {
    font-size: 7.5pt;
    color: ${DOC_COLORS.muted};
    line-height: 1.35;
  }
  .doc-tx-refund-reason {
    font-size: 7pt;
    color: ${DOC_COLORS.muted};
    margin-top: 2px;
    font-style: italic;
  }
  .doc-amount-muted {
    color: ${DOC_COLORS.muted};
    text-decoration: line-through;
  }
  .doc-amount-danger { color: #b91c1c; }
  .doc-tx-status-refunded {
    color: #b91c1c;
    font-weight: 600;
  }
  .doc-subtitle-line {
    margin-top: 6px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 8pt;
    color: ${DOC_COLORS.muted};
  }

  /* Medium-density single-page invoice */
  .doc-invoice {
    font-size: 9pt;
    line-height: 1.35;
  }
  .doc-invoice .doc-top-row { margin-bottom: 6px; }
  .doc-invoice .doc-title { font-size: 10pt; letter-spacing: 1px; }
  .doc-invoice .doc-id-badge { font-size: 9pt; }
  .doc-invoice .doc-divider { margin: 12px 0; }
  .doc-invoice .doc-brand-row { gap: 10px; margin-bottom: 0; }
  .doc-invoice .doc-brand-logo { width: 44px; height: 44px; }
  .doc-invoice .doc-brand-name { font-size: 16pt; margin: 0 0 2px; }
  .doc-invoice .doc-company-line { font-size: 7.5pt; line-height: 1.3; margin: 0 0 1px; }
  .doc-invoice .doc-invoice-meta-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin: 10px 0 8px;
  }
  .doc-invoice .doc-invoice-meta-main { flex: 1; min-width: 0; }
  .doc-invoice .doc-invoice-details-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px 14px;
  }
  .doc-invoice .doc-invoice-detail-item {
    display: flex;
    gap: 8px;
    padding: 3px 0;
    font-size: 8pt;
    border-bottom: 0;
  }
  .doc-invoice .doc-invoice-detail-label {
    flex: 0 0 auto;
    color: ${DOC_COLORS.muted};
    white-space: nowrap;
  }
  .doc-invoice .doc-invoice-detail-value {
    flex: 1;
    font-weight: 700;
    min-width: 0;
    word-break: break-word;
  }
  .doc-invoice .doc-summary-box {
    flex: 0 0 40%;
    max-width: 280px;
    margin-top: 0;
  }
  .doc-invoice .doc-summary-row {
    padding: 5px 10px;
    font-size: 8pt;
    gap: 8px;
  }
  .doc-invoice .doc-summary-row-total {
    font-size: 9pt;
    padding: 6px 10px;
  }
  .doc-invoice .doc-section-title {
    font-size: 9pt;
    margin: 8px 0 6px;
  }
  .doc-invoice .doc-table {
    font-size: 7.5pt;
    margin-bottom: 6px;
  }
  .doc-invoice .doc-table th,
  .doc-invoice .doc-table td {
    padding: 4px;
    line-height: 1.3;
    word-break: break-word;
  }
  .doc-invoice .doc-table th { font-size: 7pt; }
  .doc-invoice .doc-amount-inline { gap: 3px; }
  .doc-invoice .doc-tx-status { font-size: 7pt; line-height: 1.3; }
  .doc-invoice .doc-tx-refund-reason {
    font-size: 6.5pt;
    margin-top: 2px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .doc-invoice .doc-tx-ref {
    max-width: 90px;
    word-break: break-word;
  }
  .doc-invoice .doc-tx-ref-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .doc-invoice .doc-notes-box {
    margin-top: 10px;
    padding: 8px 10px;
  }
  .doc-invoice .doc-notes-title { font-size: 7.5pt; margin-bottom: 4px; }
  .doc-invoice .doc-notes-body { font-size: 8pt; line-height: 1.35; }
  .doc-invoice .doc-footer-box {
    margin-top: 10px;
    padding-top: 8px;
  }
  .doc-invoice .doc-footer-title { font-size: 7.5pt; margin-bottom: 3px; }
  .doc-invoice .doc-footer-line { font-size: 7.5pt; line-height: 1.3; }
  .doc-invoice .doc-footer-thanks {
    margin-top: 10px;
    font-size: 7.5pt;
  }
  .doc-invoice .doc-muted { font-size: 8pt; }
  .doc-shipments-pricing-table {
    table-layout: fixed;
  }
  .doc-shipments-pricing-table th:nth-child(1) { width: 8%; }
  .doc-shipments-pricing-table th:nth-child(2) { width: 14%; }
  .doc-shipments-pricing-table th:nth-child(3) { width: 14%; }
  .doc-shipments-pricing-table th:nth-child(4) { width: 7%; }
  .doc-shipments-pricing-table th:nth-child(5) { width: 11%; }
  .doc-shipments-pricing-table th:nth-child(6) { width: 33%; }
  .doc-shipments-pricing-table th:nth-child(7) { width: 13%; }
  .doc-shipments-pricing-table--with-weight th:nth-child(1) { width: 7%; }
  .doc-shipments-pricing-table--with-weight th:nth-child(2) { width: 12%; }
  .doc-shipments-pricing-table--with-weight th:nth-child(3) { width: 12%; }
  .doc-shipments-pricing-table--with-weight th:nth-child(4) { width: 6%; }
  .doc-shipments-pricing-table--with-weight th:nth-child(5) { width: 10%; }
  .doc-shipments-pricing-table--with-weight th:nth-child(6) { width: 9%; }
  .doc-shipments-pricing-table--with-weight th:nth-child(7) { width: 29%; }
  .doc-shipments-pricing-table--with-weight th:nth-child(8) { width: 15%; }
  .doc-shipments-pricing-table td {
    vertical-align: top;
  }
  .doc-shipments-pricing-table .doc-col-weight {
    text-align: end;
    white-space: normal;
    padding-inline: 2px;
    font-size: 7pt;
    max-width: 0;
  }
  .doc-shipments-pricing-table .doc-weight-cell {
    display: inline-block;
    max-width: 100%;
    text-align: end;
  }
  .doc-shipments-pricing-table .doc-weight-value {
    display: block;
    font-variant-numeric: tabular-nums;
    line-height: 1.15;
    overflow-wrap: anywhere;
  }
  .doc-shipments-pricing-table .doc-weight-unit {
    display: block;
    font-size: 6pt;
    line-height: 1.1;
    opacity: 0.85;
  }
  .doc-shipments-pricing-table .doc-shipment-identity {
    font-weight: 700;
    text-align: center;
  }
  .doc-shipments-pricing-table .doc-cell-center {
    text-align: center;
  }
  .doc-shipments-pricing-table .doc-cell-number,
  .doc-shipments-pricing-table .doc-cell-amount {
    text-align: end;
    white-space: nowrap;
  }
  .doc-shipments-pricing-table .doc-shipment-first-row td {
    border-top-width: 2px;
  }
  .doc-shipments-pricing-table tbody tr:first-child td {
    border-top-width: 1px;
  }
  .doc-shipment-subtotal-row td {
    background: ${DOC_COLORS.boxBg};
    font-weight: 700;
  }
  .doc-invoice-total-row td {
    background: #eef2f7;
    color: ${DOC_COLORS.accent};
    font-size: 8.5pt;
    font-weight: 700;
  }
  .doc-rule-excluded td {
    color: ${DOC_COLORS.muted};
  }
  .doc-rule-excluded .doc-amount-inline {
    text-decoration: line-through;
  }
  .doc-rule-excluded-tag {
    margin-inline-start: 4px;
    font-size: 6.5pt;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  @media print {
    .doc-invoice .doc-page,
    .doc-invoice {
      page-break-inside: avoid;
    }
    .doc-invoice .doc-table tr {
      page-break-inside: avoid;
    }
  }
`;
